import { Trash2 } from "lucide-react";
import { TextField, TextAreaField, FieldShell } from "@/components/forms";
import { cn } from "@/utils";
import type { WorkExperience } from "./types";
import { APPLICATION_LIMITS, type ExperienceField, type FieldErrors } from "./validation";

interface ExperienceCardProps {
  index: number;
  item: WorkExperience;
  /** True while the entry has any problem, shown or not yet shown. */
  incomplete: boolean;
  /** Messages to show now, per field (see `visibleErrors`). */
  errors?: FieldErrors<ExperienceField>;
  onUpdate: (field: keyof WorkExperience, value: string | boolean) => void;
  onRemove: () => void;
}

/** A single editable work-experience entry rendered as a numbered glass card. */
export function ExperienceCard({
  index,
  item,
  incomplete,
  errors = {},
  onUpdate,
  onRemove,
}: ExperienceCardProps) {
  return (
    <div
      className={cn(
        "relative rounded-2xl border p-5 transition-colors",
        incomplete ? "border-destructive/40" : "",
      )}
      style={{
        background: "var(--landing-surface)",
        borderColor: incomplete
          ? undefined
          : "var(--landing-glass-border)",
      }}
    >
      <div className="mb-4 flex items-center justify-between">
        <span
          className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold"
          style={{
            background: "rgba(var(--acc2-rgb),0.1)",
            color: "#00B0D8",
            border: "1px solid rgba(var(--acc2-rgb),0.22)",
          }}
        >
          {index + 1}
        </span>
        <button
          type="button"
          onClick={onRemove}
          className="rounded-lg p-1.5 transition-colors hover:bg-destructive/10 hover:text-destructive"
          style={{ color: "var(--landing-text-dim)" }}
          aria-label="Remove experience"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField
          label="Company"
          required
          value={item.company}
          onChange={(v) => onUpdate("company", v)}
          error={errors.company}
          placeholder="Company name"
        />
        <TextField
          label="Position"
          required
          value={item.position}
          onChange={(v) => onUpdate("position", v)}
          error={errors.position}
          placeholder="Job title"
        />
        <FieldShell label="Start date" required error={errors.startDate}>
          <input
            type="month"
            value={item.startDate}
            onChange={(e) => onUpdate("startDate", e.target.value)}
            aria-invalid={errors.startDate ? true : undefined}
            className={cn(
              "auth-input h-11 w-full rounded-xl px-3.5 text-sm outline-none",
              errors.startDate && "!border-destructive/60",
            )}
            style={{ color: "var(--landing-text)" }}
          />
        </FieldShell>
        <FieldShell label="End date" required={!item.isCurrent} error={errors.endDate}>
          <input
            type="month"
            value={item.endDate}
            onChange={(e) => onUpdate("endDate", e.target.value)}
            disabled={item.isCurrent}
            aria-invalid={errors.endDate ? true : undefined}
            className={cn(
              "auth-input h-11 w-full rounded-xl px-3.5 text-sm outline-none",
              item.isCurrent && "opacity-50",
              errors.endDate && "!border-destructive/60",
            )}
            style={{ color: "var(--landing-text)" }}
          />
        </FieldShell>
      </div>

      <label className="mt-3 inline-flex cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          checked={item.isCurrent}
          onChange={(e) => onUpdate("isCurrent", e.target.checked)}
          className="h-4 w-4 rounded border-[color:var(--landing-glass-border)] accent-[var(--acc2)]"
        />
        <span className="text-xs font-medium" style={{ color: "var(--landing-text-secondary)" }}>
          I currently work here
        </span>
      </label>

      <div className="mt-3">
        <TextAreaField
          label="What you did"
          value={item.description}
          onChange={(v) => onUpdate("description", v)}
          error={errors.description}
          countLimit={APPLICATION_LIMITS.WORK_DESCRIPTION_MAX}
          placeholder="Describe your role, scope, and a couple of measurable wins…"
          rows={3}
        />
      </div>
    </div>
  );
}
